import { useEffect, useMemo, useState } from "react";
import Api from "../../services/api";
import { Breadcrumbs } from "../../components/breadcrumbs/index.jsx";
import { Pagination } from "../../components/pagination/index.js";

const focusOptions = ["career", "skills", "ai", "team"];

const display = (value) => {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }
  return value || "-";
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

const DetailRow = ({ label, value }) => (
  <div className="grid grid-cols-12 gap-3 border-b py-2 last:border-b-0">
    <dt className="col-span-12 font-semibold text-gray-700 md:col-span-4">{label}</dt>
    <dd className="col-span-12 break-words text-gray-900 md:col-span-8">{display(value)}</dd>
  </div>
);

export const SurveyResponses = () => {
  const [responses, setResponses] = useState(null);
  const [params, setParams] = useState({ first: 10 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [selected, setSelected] = useState(null);

  const rows = responses?.data || [];

  const fetchResponses = async (nextParams = params) => {
    setLoading(true);
    const response = await Api.plSurveyResponses(nextParams);
    setLoading(false);
    if (response?.status === 200) {
      setResponses(response.data.data);
    }
  };

  useEffect(() => {
    fetchResponses();
  }, []);

  const selectedAnswers = useMemo(() => selected?.answers || {}, [selected]);

  const handleSearch = () => {
    fetchResponses({ ...params, page: 1 });
  };

  const handleClear = () => {
    const cleared = { first: 10 };
    setParams(cleared);
    fetchResponses(cleared);
  };

  const handlePage = (page) => {
    const nextParams = { ...params, page };
    setParams(nextParams);
    fetchResponses(nextParams);
  };

  const downloadBlob = (response, fallbackName) => {
    const contentDisposition = response?.headers?.["content-disposition"];
    let filename = fallbackName;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) {
        filename = match[1];
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async (type, id = null) => {
    const key = `${type}-${id || "all"}`;
    setExporting(key);

    const response = await Api.exportPlSurveyResponses({
      type,
      id,
      params: id ? {} : params,
    });

    setExporting("");

    if (response?.status === 200) {
      downloadBlob(
        response,
        id
          ? `pl-survey-response-${id}.${type === "excel" ? "xlsx" : "pdf"}`
          : `pl-survey-responses.${type === "excel" ? "xlsx" : "pdf"}`
      );
    }
  };

  return (
    <>
      <Breadcrumbs breadcrumbs={[{ name: "Survey Responses", link: "" }]} />
      <div className="mx-3 lg:mx-10">
        <div className="mb-4 flex flex-col gap-3 rounded-lg border bg-white p-4 shadow-sm md:flex-row md:items-end">
          <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
            Search
            <input
              className="h-10 rounded-md border px-3 outline-none focus:border-orange-500"
              value={params.search || ""}
              onChange={(event) => setParams({ ...params, search: event.target.value })}
              placeholder="Email, role, goal"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium text-gray-700">
            Focus
            <select
              className="h-10 rounded-md border px-3 outline-none focus:border-orange-500"
              value={params.focus || ""}
              onChange={(event) => setParams({ ...params, focus: event.target.value })}
            >
              <option value="">All</option>
              {focusOptions.map((focus) => (
                <option key={focus} value={focus}>
                  {focus}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={handleSearch}
            className="h-10 rounded-md bg-orange-500 px-5 font-semibold text-white transition hover:bg-orange-600"
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="h-10 rounded-md border border-gray-300 px-5 font-semibold text-gray-700 transition hover:bg-gray-100"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => handleExport("excel")}
            disabled={!!exporting}
            className="h-10 rounded-md bg-green-600 px-5 font-semibold text-white transition hover:bg-green-700 disabled:opacity-60"
          >
            {exporting === "excel-all" ? "Downloading..." : "Download All Excel"}
          </button>
          <button
            type="button"
            onClick={() => handleExport("pdf")}
            disabled={!!exporting}
            className="h-10 rounded-md bg-red-600 px-5 font-semibold text-white transition hover:bg-red-700 disabled:opacity-60"
          >
            {exporting === "pdf-all" ? "Downloading..." : "Download All PDF"}
          </button>
        </div>

        <div className="overflow-auto rounded-lg border bg-white shadow-sm">
          <table className="min-w-[1200px] w-full text-left text-sm">
            <thead className="bg-gray-100 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Last Updated</th>
                <th className="px-4 py-3">Email/User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Level</th>
                <th className="px-4 py-3">Region</th>
                <th className="px-4 py-3">Goal</th>
                <th className="px-4 py-3">AI sentiment</th>
                <th className="px-4 py-3">Topics</th>
                <th className="px-4 py-3">Formats</th>
                <th className="px-4 py-3">Opt-in</th>
                <th className="px-4 py-3">Focus</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-8 text-center text-gray-500" colSpan="13">
                    Loading responses...
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((row) => (
                  <tr key={row.id} className="border-t align-top hover:bg-orange-50/40">
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(row.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(row.updated_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{row.email}</div>
                      {row.user && (
                        <div className="text-xs text-gray-500">
                          {display(`${row.user.firstname || ""} ${row.user.lastname || ""}`.trim() || row.user.username)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">{display(row.answers?.role)}</td>
                    <td className="px-4 py-3">{display(row.answers?.level)}</td>
                    <td className="px-4 py-3">{display(row.answers?.region)}</td>
                    <td className="px-4 py-3">{display(row.answers?.goal)}</td>
                    <td className="px-4 py-3">{display(row.answers?.ai_sentiment)}</td>
                    <td className="px-4 py-3">{display(row.answers?.topics)}</td>
                    <td className="px-4 py-3">{display(row.answers?.formats)}</td>
                    <td className="px-4 py-3">{display(row.answers?.optin)}</td>
                    <td className="px-4 py-3">{display(row.focus)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setSelected(row)}
                          className="rounded-md bg-orange-500 px-3 py-1.5 font-semibold text-white transition hover:bg-orange-600"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => handleExport("excel", row.id)}
                          disabled={!!exporting}
                          className="rounded-md bg-green-600 px-3 py-1.5 font-semibold text-white transition hover:bg-green-700 disabled:opacity-60"
                        >
                          {exporting === `excel-${row.id}` ? "..." : "Excel"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleExport("pdf", row.id)}
                          disabled={!!exporting}
                          className="rounded-md bg-red-600 px-3 py-1.5 font-semibold text-white transition hover:bg-red-700 disabled:opacity-60"
                        >
                          {exporting === `pdf-${row.id}` ? "..." : "PDF"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-4 py-8 text-center text-gray-500" colSpan="13">
                    No survey responses found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          total={responses?.last_page}
          current={responses?.current_page}
          func={handlePage}
        />
      </div>

      {selected && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-lg bg-white shadow-xl">
            <div className="sticky top-0 flex items-center justify-between border-b bg-white p-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Survey Response</h2>
                <p className="text-sm text-gray-500">{selected.email}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="rounded-full px-3 py-1 text-2xl leading-none text-gray-500 hover:bg-gray-100"
              >
                x
              </button>
            </div>
            <dl className="p-5 text-sm">
              <DetailRow label="Submitted" value={formatDate(selected.created_at)} />
              <DetailRow label="Last updated" value={formatDate(selected.updated_at)} />
              <DetailRow label="Role" value={selectedAnswers.role} />
              <DetailRow label="Level" value={selectedAnswers.level} />
              <DetailRow label="Region" value={selectedAnswers.region} />
              <DetailRow label="Goal" value={selectedAnswers.goal} />
              <DetailRow label="AI sentiment" value={selectedAnswers.ai_sentiment} />
              <DetailRow label="Topics" value={selectedAnswers.topics} />
              <DetailRow label="Formats" value={selectedAnswers.formats} />
              <DetailRow label="Leader access" value={selectedAnswers.leader_access} />
              <DetailRow label="Time/month" value={selectedAnswers.time} />
              <DetailRow label="Fair price/year" value={selectedAnswers.price} />
              <DetailRow label="Team plan" value={selectedAnswers.team_plan} />
              <DetailRow label="Most useful thing" value={selectedAnswers.most_useful} />
              <DetailRow label="Opt-in" value={selectedAnswers.optin} />
              <DetailRow label="Focus" value={selected.focus} />
              <DetailRow label="Page URL" value={selected.page_url} />
              <DetailRow label="User agent" value={selected.user_agent} />
            </dl>
          </div>
        </div>
      )}
    </>
  );
};
